import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, FlaskConical, Link2, Shield, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import MfaResetModal from '@/components/admin/MfaResetModal';
import MultiStepOrganisationModal from '@/components/organisation/MultiStepOrganisationModal';
import { MerchantRecord } from '@/shared/types/merchant';
import { CutoffDateDialog } from './CutoffDateDialog';
import { DuplicateMerchantDialog } from './DuplicateMerchantDialog';
import { MagicLinkDialog } from './MagicLinkDialog';
import { hasSubmittedOnboarding } from '@/shared/onboardingState';

// Merchant actions on the Settings tab (FIG-320 / REQ-038): icon-and-label
// buttons with tooltips, moved from the Merchants list's Actions column.
// Edit became Merchant Details; Delete is the danger zone at the foot.
// "Upload data" keeps the old edit form reachable because its CSV step is the
// only way for an admin to add sales CSV files to an existing merchant (where
// they should live is open in FIG-321).

interface MerchantActionsRowProps {
  merchant: MerchantRecord & { products?: unknown };
  onUpdated: () => void;
}

const ActionButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ icon, label, tooltip, onClick, disabled }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A disabled button gets no pointer events, so the tooltip sits on a wrapper. */}
        <span tabIndex={disabled ? 0 : -1} className="inline-flex">
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={onClick} disabled={disabled}>
            {icon}
            {label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent><p>{tooltip}</p></TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const MerchantActionsRow: React.FC<MerchantActionsRowProps> = ({ merchant, onUpdated }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<null | 'duplicate' | 'magic-link' | 'mfa' | 'cutoff' | 'upload'>(null);
  const close = (isOpen: boolean) => {
    if (!isOpen) setOpen(null);
  };

  return (
    <div role="group" aria-label="Merchant actions" className="flex flex-wrap items-center gap-2">
      {merchant.is_test_clone ? (
        <ActionButton
          icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
          label={merchant.data_cutoff_date ? `Cutoff ${merchant.data_cutoff_date}` : 'Set cutoff'}
          tooltip="Change the data cutoff date of this test copy"
          onClick={() => setOpen('cutoff')}
        />
      ) : (
        <ActionButton
          icon={<FlaskConical className="h-4 w-4" aria-hidden="true" />}
          label="Duplicate"
          tooltip="Create a [TEST] copy of this merchant to run test scenarios"
          onClick={() => setOpen('duplicate')}
        />
      )}
      <ActionButton
        icon={<Upload className="h-4 w-4" aria-hidden="true" />}
        label="Upload data"
        tooltip="Open the edit form to upload CSV data for this merchant"
        onClick={() => setOpen('upload')}
      />
      <ActionButton
        icon={<Link2 className="h-4 w-4" aria-hidden="true" />}
        label="Resend magic link"
        tooltip="Email a new sign-in link to the point of contact"
        onClick={() => setOpen('magic-link')}
      />
      <ActionButton
        icon={<Shield className="h-4 w-4" aria-hidden="true" />}
        label="Reset MFA"
        tooltip={merchant.poc_email
          ? "Turn off the point of contact's MFA and email a reset link"
          : 'Add a POC email in Merchant Details first'}
        onClick={() => setOpen('mfa')}
        disabled={!merchant.poc_email}
      />

      <DuplicateMerchantDialog
        open={open === 'duplicate'}
        onOpenChange={close}
        merchant={merchant}
        onDuplicated={(cloneId) => navigate(`/organisation/${cloneId}`)}
      />
      <CutoffDateDialog open={open === 'cutoff'} onOpenChange={close} merchant={merchant} onSaved={onUpdated} />
      <MultiStepOrganisationModal
        open={open === 'upload'}
        onOpenChange={close}
        onSuccess={async () => onUpdated()}
        editMode
        organizationId={merchant.id}
        isDraft={!hasSubmittedOnboarding(merchant)}
        isAdminEdit
      />
      <MagicLinkDialog open={open === 'magic-link'} onOpenChange={close} merchant={merchant} />
      {merchant.poc_email && (
        <MfaResetModal
          open={open === 'mfa'}
          onOpenChange={close}
          userEmail={merchant.poc_email}
          userName={merchant.poc_name ?? ''}
          onSuccess={onUpdated}
        />
      )}
    </div>
  );
};

export default MerchantActionsRow;
